export const ExpressionPlaceholderTypes = [ "string", "role", "channel", "user", "text", "number" ] as const;

export type ExpressionPlaceholderType = Exclude<typeof ExpressionPlaceholderTypes[number], "text">;

export type ExpressionPlaceholder = {
    key: string;
    raw: string;
    type: ExpressionPlaceholderType;
    label: string;
};

const PlaceholderPattern = /@([a-zA-Z_][a-zA-Z0-9_]*)::"([^"]+)"|\b([a-zA-Z_][a-zA-Z0-9_]*)::([^,)}\]\n]+)/g;
const PlaceholderTypes = new Set<string>(ExpressionPlaceholderTypes);

function normalizePlaceholderType(type: string): ExpressionPlaceholderType | null {
    if (!PlaceholderTypes.has(type))
        return null;

    return type === "text"
        ? "string"
        : type as ExpressionPlaceholderType;
}

export function parseExpressionPlaceholders(expression: string): ExpressionPlaceholder[] {
    const placeholders = new Map<string, ExpressionPlaceholder>();
    let match: RegExpExecArray | null;

    while ((match = PlaceholderPattern.exec(expression))) {
        const rawType = (match[1] ?? match[3] ?? "").trim();
        const label = (match[2] ?? match[4] ?? "").trim();
        const type = normalizePlaceholderType(rawType);

        if (!type || !label)
            continue;

        const key = `${type}::${label}`;

        placeholders.set(key, {
            key,
            raw: match[0]!,
            type,
            label,
        });
    }

    return [ ...placeholders.values() ];
}

export function quoteDslString(value: string): string {
    return `"${value.replace(/\\/g, "\\\\").replace(/"/g, "\\\"")}"`;
}

export function resolveExpressionPlaceholders(expression: string, values: Record<string, string>) {
    let resolved = expression;
    const missing: ExpressionPlaceholder[] = [];

    for (const placeholder of parseExpressionPlaceholders(expression)) {
        const value = values[placeholder.key]?.trim();

        if (!value) {
            missing.push(placeholder);
            continue;
        }

        const replacement = placeholder.type === "number"
            ? value
            : quoteDslString(value);

        resolved = resolved.replaceAll(placeholder.raw, replacement);
    }

    return {
        resolved,
        missing,
    };
}
